/** XeAgents.ai **/
import { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Play,
  Pause,
  Volume2,
  FileText,
  ExternalLink,
  Calendar,
  MessageSquare,
  Loader2
} from "lucide-react";
import { Link } from "wouter";
import { AuthStorage } from "@/lib/auth-storage";
import { useTranslation } from "react-i18next";

interface Contact {
  id: string;
  firstName: string;
  lastName?: string;
  phone: string;
  email?: string;
}

interface Call {
  id: string;
  status: string;
  duration: number | null;
  classification: string | null;
  sentiment: string | null;
  creditsDeducted: number | null;
  aiSummary: string | null;
  transcript: string | null;
  recordingUrl: string | null;
  metadata: Record<string, any> | null;
}

interface ExpandedRecipientRowProps {
  contact: Contact;
  call: Call | undefined;
  campaignId: string;
}

export function ExpandedRecipientRow({ contact, call, campaignId }: ExpandedRecipientRowProps) {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Extract topics from metadata or transcript
  const topics = call?.metadata?.topics || call?.metadata?.interests || [];
  const appointmentBooked = call?.metadata?.appointmentBooked;
  const appointmentDate = call?.metadata?.appointmentDate;

  // Fetch recording when needed
  const fetchRecording = async () => {
    if (!call?.id || audioUrl) return;

    setIsLoadingAudio(true);
    try {
      const headers: Record<string, string> = {};
      const authHeader = AuthStorage.getAuthHeader();
      if (authHeader) {
        headers.Authorization = authHeader;
      }

      const response = await fetch(`/api/calls/${call.id}/recording`, {
        headers,
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
      }
    } catch (error) {
      console.error("Failed to fetch recording:", error);
    } finally {
      setIsLoadingAudio(false);
    }
  };

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setAudioDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [audioUrl]);

  const togglePlayPause = async () => {
    if (!audioUrl) {
      await fetchRecording();
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!call) {
    return (
      <div className="px-4 py-6 bg-muted/30 border-t">
        <p className="text-sm text-muted-foreground text-center">
          No call data available yet
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 bg-muted/30 border-t">
      <div className="grid gap-4 md:grid-cols-2">
        {/* AI Summary */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            AI Summary
          </h4>
          {call.aiSummary ? (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {call.aiSummary.length > 250
                ? `${call.aiSummary.substring(0, 250)}...`
                : call.aiSummary}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              No summary available
            </p>
          )}

          {/* Topics */}
          {Array.isArray(topics) && topics.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {topics.slice(0, 5).map((topic: string, idx: number) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {topic}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Audio Player & Actions */}
        <div className="space-y-4">
          {/* Mini Audio Player */}
          {(call.recordingUrl || call.status === 'completed') && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Volume2 className="h-4 w-4" />
                Recording
              </h4>
              <div className="flex items-center gap-3 p-3 bg-background rounded-lg border">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={togglePlayPause}
                  disabled={isLoadingAudio}
                >
                  {isLoadingAudio ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isPlaying ? (
                    <Pause className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </Button>

                {/* Progress Bar */}
                <div className="flex-1">
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{
                        width: audioDuration > 0
                          ? `${(currentTime / audioDuration) * 100}%`
                          : "0%"
                      }}
                    />
                  </div>
                </div>

                {/* Time Display */}
                <span className="text-xs text-muted-foreground min-w-[60px] text-right">
                  {formatTime(currentTime)} / {formatTime(audioDuration || call.duration || 0)}
                </span>

                {audioUrl && (
                  <audio ref={audioRef} src={audioUrl} preload="metadata" />
                )}
              </div>
            </div>
          )}

          {/* Appointment Info */}
          {appointmentBooked && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-green-600" />
              <span className="text-green-600 font-medium">
                Appointment booked
                {appointmentDate && `: ${appointmentDate}`}
              </span>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex gap-2">
            <Link href={`/app/calls/${call.id}`}>
              <Button variant="outline" size="sm" className="gap-2">
                <FileText className="h-4 w-4" />
                Full Transcript
              </Button>
            </Link>
            <Link href={`/app/calls/${call.id}`}>
              <Button variant="outline" size="sm" className="gap-2">
                <ExternalLink className="h-4 w-4" />
                View Details
              </Button>
            </Link>
          </div>

          {/* Credits Info */}
          {call.creditsDeducted !== null && call.creditsDeducted > 0 && (
            <p className="text-xs text-muted-foreground">
              Credits used: {call.creditsDeducted.toFixed(1)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
